import React from 'react';
import LoadingDots from './LoadingDots';
import Icon from './Icon';
import {ajax, postJson, resolveRoute} from '../helpers/ajax'
import {updateState} from "../helpers/react-helpers";
import ManualChargeForm from "./ManualChargeForm";
import StripeChargeUncapturedForm from "./StripeChargeUncapturedForm";
import memoize from '../memoize';
import * as kb from '../kb';
import StripeRefundForm from './StripeRefundForm';
import PubSub from 'pubsub-js';
import * as topics from '../pubsub-topics';
import icons from '../../markons/markons';
import ActionButton from './ActionButton';
import classNames from 'classnames';
import {ExternalLink} from './external-links'
import ModalDialog from './ModalDialog'

interface PreviousPaymentsTableState {
    payments: any[],
    loading: boolean,
    refund: any,
    editPayId: string|number|null,
    chargeUncaptured: any,
}

interface PreviousPaymentsTableProps {
    bookingId: string|number;
    testMode?: boolean;
    stripeUserId?: string;
}

export default class PreviousPaymentsTable extends React.PureComponent<PreviousPaymentsTableProps, PreviousPaymentsTableState> {

    subToken: any;
    deletePayment = memoize((id: any) => (ev: any) => {
        ev.preventDefault();
        if (confirm("Are you sure you want to delete this payment? Note: this will not refund your client.")) {
            postJson<void,{id:string}>(resolveRoute('payments.delete'), {id}, {method: 'DELETE'})
                .then(() => {
                    const idx = _.findIndex(this.state.payments, p => p.id == id);
                    updateState.call(this, {payments: {$splice: [[idx, 1]]}});

                    kb.notify({
                        html: "Payment deleted",
                        className: "success"
                    });
                }, () => {
                    kb.notify({
                        html: "Delete failed",
                        className: "error"
                    });
                });
        }
    });
    refundPayment = memoize((chargeId: any) => (ev: any) => {
        ev.preventDefault();
        this.setState({refund: chargeId})
    });
    editPayment = memoize((payId: any) => (ev: any) => {
        ev.preventDefault();
        this.setState({editPayId: payId})
    });
    chargeUncaptured = memoize((chargeId: any) => (ev: any) => {
        ev.preventDefault();
        this.setState({chargeUncaptured: chargeId})
    });

    constructor(props: any) {
        super(props);
        this.state = {
            payments: [],
            loading: true,
            refund: null,
            editPayId: null,
            chargeUncaptured: null,
        }
    }

    componentDidMount() {
        this.refresh().finally(() => {
            this.setState({loading: false});
        });
        // TODO: replace PubSub with `createGlobalState`
        this.subToken = PubSub.subscribe(topics.PaymentEntered, this.refresh);
    }

    componentWillUnmount() {
        PubSub.unsubscribe(this.subToken);
    }

    refresh = () => {
        return ajax(resolveRoute('api.previousPayments', {
            bookingId: this.props.bookingId,
        }))
            .then((res: any) => {
                if (res.ok) {
                    this.setState({payments: res.data});
                }
            });

    };

    afterRefund = () => {
        this.setState({refund: null})
    };

    afterEditPay = () => {
        kb.notify({
            html: "Payment updated",
            className: "success"
        });
        this.setState({editPayId: null})
    };

    cancelEditPay = () => {
        this.setState({editPayId: null})
    };

    afterChargeUncaptured = () => {
        kb.notify({
            html: "Card charged",
            className: "success"
        });
        this.setState({chargeUncaptured: null})
    };

    cancelChargeUncaptured = () => {
        this.setState({chargeUncaptured: null})
    };

    render() {
        return (
            <div>
                <table className="index-table no-links">
                    <thead>
                        <tr>
                            <th className="shrink">Booking</th>
                            <th className="shrink">Pay Date</th>
                            <th className="shrink number">Amount</th>
                            <th className="shrink">Currency</th>
                            <th className="shrink">Payment Source</th>
                            <th className="shrink">Method</th>
                            <th/>
                        </tr>
                    </thead>
                    <tbody>
                        {this.renderBody()}
                    </tbody>
                </table>

                {this.state.refund ?
                    <ModalDialog title="Refund Payment">
                        <StripeRefundForm chargeId={this.state.refund} afterSave={this.afterRefund}
                                          onCancel={this.afterRefund}/>
                    </ModalDialog>
                    : null}

                {this.state.editPayId ?
                    <ModalDialog title="Edit Payment">
                        <ManualChargeForm bookingId={this.props.bookingId} paymentId={this.state.editPayId}
                                          afterSave={this.afterEditPay} onCancel={this.cancelEditPay}/>
                    </ModalDialog>
                    : null}

                {this.state.chargeUncaptured ?
                    <ModalDialog title="Capture Payment">
                        <StripeChargeUncapturedForm chargeId={this.state.chargeUncaptured}
                                                    afterSave={this.afterChargeUncaptured}
                                                    onCancel={this.cancelChargeUncaptured}/>
                    </ModalDialog>
                    : null}
            </div>
        )
    }

    renderBody() {
        if (this.state.loading) {
            return (
                <tr>
                    <td colSpan={7} style={{textAlign: 'center'}}>
                        <LoadingDots/>
                    </td>
                </tr>
            );
        }

        if (!this.state.payments.length) {
            return (
                <tr>
                    <td colSpan={8} style={{textAlign: 'center'}}>
                        <span>No payments made.</span>
                    </td>
                </tr>
            );
        }

        return this.state.payments.map((pay: any) => {
            const amount = kb.formatCurrencyIntl(pay.amount, pay.currency);
            const amountClasses = ['shrink', 'number'];
            if (pay.transaction_sum == 0) {
                amountClasses.push('charge-zero-sum');
                // amount = <del className="zero-sum">{amount}</del>;
            }
            let amountTitle;


            if (pay.captured) {
                if (pay.transaction_sum == 0) {
                    if (pay.amount > 0) {
                        amountTitle = "Payment succeeded and later refunded";
                    } else {
                        amountTitle = "Payment fully refunded";
                    }
                } else {
                    if (pay.amount > 0) {
                        amountTitle = "Payment succeeded";
                    } else {
                        amountTitle = "Payment partially refunded";
                    }
                }
            } else {
                if (pay.amount < 0) {
                    amountTitle = "Pre-auth released";
                } else {
                    if (pay.transaction_sum == 0) {
                        amountTitle = "Pre-auth succeeded and later released";
                    } else if (pay.expired) {
                        amountTitle = "Pre-auth expired";
                    } else {
                        amountTitle = "Pre-authorized but not charged";
                    }
                }
            }


            if (!pay.captured) {
                amountClasses.push('charge-uncaptured');
            }
            if (pay.amount > 0) {
                amountClasses.push('charge-payment');
            } else if (pay.amount < 0) {
                amountClasses.push('charge-refund');
            }
            if (pay.expired) {
                amountClasses.push('charge-expired');
            }
            const info = [];
            if (pay.transaction_id) {
                info.push(`Transaction ID: ${pay.transaction_id}`);
            }
            if (pay.authorization_nbr) {
                info.push(`Authorization #: ${pay.authorization_nbr}`);
            }
            return (
                <tr key={pay.id}>
                    <td className="shrink tcenter"><a
                        href={resolveRoute('bookings.show', {id: pay.booking_id})}>#{pay.booking_number}</a></td>
                    <td className="shrink"
                        title={`Entered on ${pay.enter_date} by ${pay.enter_user}`}>{pay.pay_date}</td>
                    <td className={classNames(amountClasses)} title={amountTitle}>{amount}</td>
                    <td className="shrink tcenter">{pay.currency}</td>
                    <td className="shrink">{pay.source || '--'}</td>
                    <td className="shrink">
                        {pay.method_code === 'STRIPE'
                            ? <ExternalLink href={`https://dashboard.stripe.com/${this.props.testMode ? 'test/'+(this.props.stripeUserId ? `connect/accounts/${this.props.stripeUserId}/` : '') : ''}payments/${encodeURIComponent(pay.transaction_id)}`}>{pay.method}</ExternalLink>
                            : pay.method}
                    </td>
                    <td>
                        <div className="nowrap" style={{margin: "0 -3px -3px 0"}}>
                            {info.length ?
                                <span className="help-icon" title={info.join('\n')}
                                      style={{margin: '0 3px 3px 0'}}><Icon name={icons.faInfoCircle}/></span> :
                                <span className="help-icon disabled" title="No info recorded"
                                      style={{margin: '0 3px 3px 0'}}><Icon name={icons.faInfoCircle}/></span>
                            }
                            <IconButton onClick={this.deletePayment(pay.id)} title="Delete" className="btn-delete"
                                        icon={icons.ionTrashA}/>
                            {pay.method_code === 'STRIPE' && pay.amount > 0 && pay.transaction_sum > 0 ?
                                <IconButton onClick={this.refundPayment(pay.stripe_payment_intent_id || pay.transaction_id)}
                                            title={pay.captured ? "Refund" : "Release"} className="btn-new"
                                            icon={icons.ionRefresh}/> : null}
                            {pay.method_code === 'STRIPE' && pay.amount > 0 && pay.transaction_sum > 0 && !pay.captured ?
                                <IconButton onClick={this.chargeUncaptured(pay.stripe_payment_intent_id || pay.transaction_id)}
                                            title="Charge Credit Card" className="btn-new"
                                            icon={icons.ionCard}/> : null}
                            {pay.method_code === 'MANUAL' ?
                                <IconButton onClick={this.editPayment(pay.id)} title="Edit" icon={icons.faPencil}
                                            className="btn-edit"/> : null}
                        </div>
                    </td>
                </tr>
            )
        });
    }
}

function IconButton({icon, onClick, title, className }: any) {
    return <ActionButton onClick={onClick} className={className}><Icon name={icon}/> {title}</ActionButton>;
}
